import { calculateSimilarityScore } from '@chronosphereio/ai';
import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics, useDataQueryEditor } from '@/plugin-system';
import { useVizFrameworkConfig } from '@/plugin-system/model/config';
import { useQueryAssistant } from './query-assistant-context';

export function useIsQueryAssistantEnabled() {
  const config = useVizFrameworkConfig();
  const suggestQueryFn = useSuggestQueryFn();
  return (config?.featureFlags.aiPromQLQueryGeneration ?? false) && Boolean(suggestQueryFn);
}

export function useSuggestQueryFn() {
  const dataQueryEditorCtx = useDataQueryEditor();
  return dataQueryEditorCtx?.onSuggestQuery;
}

/**
 * Add keyboard shortcuts to toggle the query assistant.
 * - `Cmd + I`
 * - `Cmd + K` when the container is in focus.
 */
export function useQueryAssistantShortcut() {
  const isAssistantEnabled = useIsQueryAssistantEnabled();
  const { isAssistantOpen, openAssistant, closeAssistant } = useQueryAssistant();

  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (
        (e.metaKey && e.key === 'i') ||
        // Including `Cmd + K` for those who might have muscle memory for it.
        // This shortcut is "context-sensitive" in that it only works when the container is in focus.
        // Otherwise, it would conflict with the shorcut for global search.
        (e.metaKey && !e.shiftKey && e.key === 'k' && containerRef.current?.contains(document.activeElement))
      ) {
        e.preventDefault();
        e.stopPropagation();
        if (isAssistantOpen) {
          closeAssistant();
        } else {
          openAssistant();
        }
      }
    },
    [isAssistantOpen, openAssistant, closeAssistant]
  );

  useEffect(() => {
    if (!isAssistantEnabled) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, isAssistantEnabled]);

  return { containerRef };
}

export function useQueryAssistantAcceptAndReject({ feature, evalName }: { feature: string; evalName: string }) {
  const analytics = useAnalytics();
  const { query, suggestion } = useQueryAssistant();

  const onAccept = useCallback(() => {
    if (!suggestion || !analytics?.trackEval) return;

    const currentQuery = query;
    const suggestedQuery = suggestion?.query ?? '';
    const { version, input, existingQuery } = suggestion?.metadata ?? {};

    analytics.trackEval({
      feature,
      input: input,
      output: suggestedQuery,
      eval: { name: evalName, score: calculateSimilarityScore(suggestedQuery, currentQuery) / 100 },
      version: version ?? '0',
      metadata: { existingQuery },
    });
  }, [suggestion, analytics, feature, evalName, query]);

  const onReject = useCallback(() => {
    if (!suggestion || !analytics?.trackEval) return;

    const { version, input, existingQuery } = suggestion?.metadata ?? {};

    analytics.trackEval({
      feature,
      input: input,
      output: suggestion.query,
      eval: { name: evalName, score: 0 },
      version: version ?? '0',
      metadata: { existingQuery },
    });
  }, [suggestion, analytics, feature, evalName]);

  return { onAccept, onReject };
}
